import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import PublicLanguageSwitcher from '../ui/PublicLanguageSwitcher';
import PublicBackToHomeLink from '../ui/PublicBackToHomeLink';
import HeroDepthBackground from '../ui/HeroDepthBackground';
import { useAuth } from '../../contexts/AuthContext';
import { resolveAuthError } from '../../utils/apiError';
import toast from 'react-hot-toast';
import {
  Building2,
  Shield,
  Users,
  ArrowRight,
  Eye,
  EyeOff,
} from 'lucide-react';
import TermsAgreementCheckbox from '../ui/TermsAgreementCheckbox';
import Button from '../ui/Button';
import BrandMark from '../ui/BrandMark';
import {
  validateEmail,
  validatePassword,
  validatePasswordMatch,
  validateRequired,
  validateFields,
} from '../../utils/formValidation';

function premiumFieldClass(hasError, extra = '') {
  return [
    'input-premium w-full py-2.5 sm:py-3 text-sm sm:text-base',
    hasError ? 'ring-2 ring-error-500/40 focus:ring-error-500/40' : '',
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

function PasswordInput({ id, value, onChange, placeholder, show, onToggle, minLength, autoComplete, hasError }) {
  const { t } = useLanguage();

  return (
    <div className="relative">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        required
        minLength={minLength}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={hasError ? true : undefined}
        className={`input-premium w-full pr-11 py-2.5 sm:py-3 text-sm sm:text-base${
          hasError ? ' ring-2 ring-error-500/40 focus:ring-error-500/40' : ''
        }`}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors"
        aria-label={show ? t.common.hidePassword : t.common.showPassword}
      >
        {show ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
      </button>
    </div>
  );
}

const fadeTransition = { duration: 0.22, ease: [0.23, 1, 0.32, 1] };
const tabSpring = { type: 'spring', stiffness: 320, damping: 32 };

const EnterpriseLandingPage = () => {
  const navigate = useNavigate();
  const { t, formatMessage } = useLanguage();
  const { registerCompany, loginCompany } = useAuth();
  const [mode, setMode] = useState('login');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const validation = t.ux?.validation || {};

  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
    if (submitError) setSubmitError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const { valid, errors } = validateFields({
      companyName: validateRequired(companyName, validation.companyRequired || t.enterprise.form.companyRequired),
      email: validateEmail(email, {
        required: validation.emailRequired,
        invalid: validation.emailInvalid,
      }),
      password: validatePassword(password, {
        required: validation.passwordRequired,
        min: validation.passwordMin,
      }),
      confirmPassword: validatePasswordMatch(password, confirmPassword, validation.passwordMismatch || t.enterprise.form.passwordMismatch),
      terms: acceptedTerms ? null : (validation.termsRequired || t.legal.mustAccept),
    });
    setFieldErrors(errors);
    if (!valid) return;

    setLoading(true);
    setSubmitError('');
    const result = await registerCompany(companyName.trim(), email.trim(), password);
    setLoading(false);

    if (result.success) {
      toast.success(t.enterprise.form.success);
      navigate('/admin');
    } else {
      const errMsg = resolveAuthError(result, t, formatMessage, t.enterprise.form.registrationFailed);
      setSubmitError(errMsg);
      toast.error(errMsg);
    }
  };

  const features = [
    { icon: Building2, title: t.enterprise.features.isolated.title, description: t.enterprise.features.isolated.description },
    { icon: Users, title: t.enterprise.features.team.title, description: t.enterprise.features.team.description },
    { icon: Shield, title: t.enterprise.features.security.title, description: t.enterprise.features.security.description },
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    const { valid, errors } = validateFields({
      email: validateEmail(email, {
        required: validation.emailRequired,
        invalid: validation.emailInvalid,
      }),
      password: validatePassword(password, {
        required: validation.passwordRequired,
        min: validation.passwordMin,
      }),
    });
    setFieldErrors(errors);
    if (!valid) return;

    setLoading(true);
    setSubmitError('');
    const result = await loginCompany(email.trim(), password);
    setLoading(false);

    if (result.success) {
      toast.success(t.enterprise.login.success);
      navigate('/admin');
    } else {
      const errMsg = resolveAuthError(result, t, formatMessage, t.enterprise.login.signInFailed);
      setSubmitError(errMsg);
      toast.error(errMsg);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAcceptedTerms(false);
    setFieldErrors({});
    setSubmitError('');
  };

  return (
    <div className="app-shell relative flex min-h-[100dvh] justify-center overflow-x-hidden p-3 safe-x safe-bottom sm:p-4 md:p-6 lg:p-8 max-lg:items-start max-lg:overflow-y-auto lg:items-center lg:overflow-hidden">
      <HeroDepthBackground sceneCount={1} fixed />
      <PublicBackToHomeLink
        variant="fixed"
        label={t.enterprise.nav.backToHome}
      />
      <PublicLanguageSwitcher variant="fixed" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative z-10 my-auto mx-auto grid w-full max-w-6xl grid-cols-1 gap-4 px-3 sm:gap-8 sm:px-4 lg:grid-cols-2 lg:gap-12 items-center"
      >
        {/* Left — branding & features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center lg:text-left order-1 lg:order-1"
        >
          <div className="mb-4 sm:mb-8">
            <BrandMark variant="hero" className="mb-5 sm:mb-6 lg:mb-8" />

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg font-bold leading-snug text-secondary-900 mb-1 px-2 sm:hidden"
            >
              {t.enterprise.hero.title1}{' '}
              <span className="text-gradient">{t.enterprise.hero.titleHighlight}</span>
            </motion.h2>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-secondary-900 mb-2 sm:mb-4 hidden sm:block"
            >
              {t.enterprise.hero.title1}
              <span className="text-gradient block mt-1">{t.enterprise.hero.titleHighlight}</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-xs sm:text-base md:text-lg text-secondary-600 leading-relaxed max-w-md mx-auto lg:mx-0 px-2 sm:px-0 hidden sm:block"
            >
              {t.enterprise.hero.subtitle}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="space-y-3 sm:space-y-4 lg:space-y-6 hidden sm:block"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="feature-row"
              >
                <div className="icon-tile h-10 w-10 sm:h-12 sm:w-12 from-orange-500 to-amber-500">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-sm sm:text-base text-secondary-900">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-secondary-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right — auth card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="w-full max-w-md mx-auto order-2 lg:order-2"
        >
          <div className="card-premium shadow-premium p-4 sm:p-6 md:p-8 lg:p-10">
            <div className="text-center mb-4 sm:mb-6">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={mode}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={fadeTransition}
                >
                  <h3 className="text-lg sm:text-2xl font-bold text-secondary-900 mb-1 sm:mb-2">
                    {mode === 'login' ? t.enterprise.login.title : t.enterprise.form.title}
                  </h3>
                  <p className="text-xs sm:text-base text-secondary-600">
                    {mode === 'login' ? t.enterprise.login.subtitle : t.enterprise.form.subtitle}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="relative flex rounded-xl bg-secondary-100 p-1 mb-6 sm:mb-8">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-200 ${
                  mode === 'login'
                    ? 'text-orange-600'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                {mode === 'login' ? (
                  <motion.span
                    layoutId="enterprise-auth-tab"
                    className="absolute inset-0 rounded-lg bg-white shadow-soft"
                    transition={tabSpring}
                  />
                ) : null}
                <span className="relative">{t.enterprise.login.tab}</span>
              </button>
              <button
                type="button"
                onClick={() => switchMode('register')}
                className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-200 ${
                  mode === 'register'
                    ? 'text-orange-600'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                {mode === 'register' ? (
                  <motion.span
                    layoutId="enterprise-auth-tab"
                    className="absolute inset-0 rounded-lg bg-white shadow-soft"
                    transition={tabSpring}
                  />
                ) : null}
                <span className="relative">{t.enterprise.form.tab}</span>
              </button>
            </div>

            <AnimatePresence mode="wait" initial={false}>
              {mode === 'login' ? (
                <motion.form
                  key="login-form"
                  onSubmit={handleLogin}
                  className="space-y-3 sm:space-y-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={fadeTransition}
                >
                  <div>
                    <label htmlFor="loginEmail" className="block text-xs sm:text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      {t.enterprise.form.email}
                    </label>
                    <input
                      id="loginEmail"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFieldError('email');
                      }}
                      placeholder={t.enterprise.form.emailPlaceholder}
                      className={premiumFieldClass(Boolean(fieldErrors.email))}
                      aria-invalid={fieldErrors.email ? true : undefined}
                    />
                    {fieldErrors.email ? (
                      <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.email}</p>
                    ) : null}
                  </div>

                  <div>
                    <label htmlFor="loginPassword" className="block text-xs sm:text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      {t.enterprise.form.password}
                    </label>
                    <PasswordInput
                      id="loginPassword"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearFieldError('password');
                      }}
                      placeholder={t.enterprise.form.passwordPlaceholder}
                      show={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                      autoComplete="current-password"
                      hasError={Boolean(fieldErrors.password)}
                    />
                    {fieldErrors.password ? (
                      <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.password}</p>
                    ) : null}
                  </div>

                  {submitError ? (
                    <p role="alert" className="rounded-2xl border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700">
                      {submitError}
                    </p>
                  ) : null}

                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                    disabled={loading}
                    icon={ArrowRight}
                    iconPosition="right"
                    className="w-full py-2.5 sm:py-4 mt-4 sm:mt-6"
                  >
                    {t.enterprise.login.submit}
                  </Button>
                </motion.form>
              ) : (
                <motion.form
                  key="register-form"
                  onSubmit={handleRegister}
                  className="space-y-3 sm:space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={fadeTransition}
                >
                  <div>
                    <label htmlFor="registerCompanyName" className="block text-xs sm:text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      {t.enterprise.form.companyName}
                    </label>
                    <input
                      id="registerCompanyName"
                      type="text"
                      required
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        clearFieldError('companyName');
                      }}
                      placeholder={t.enterprise.form.companyNamePlaceholder}
                      className={premiumFieldClass(Boolean(fieldErrors.companyName))}
                      aria-invalid={fieldErrors.companyName ? true : undefined}
                    />
                    {fieldErrors.companyName ? (
                      <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.companyName}</p>
                    ) : null}
                  </div>

                  <div>
                    <label htmlFor="registerEmail" className="block text-xs sm:text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      {t.enterprise.form.email}
                    </label>
                    <input
                      id="registerEmail"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFieldError('email');
                      }}
                      placeholder={t.enterprise.form.emailPlaceholder}
                      className={premiumFieldClass(Boolean(fieldErrors.email))}
                      aria-invalid={fieldErrors.email ? true : undefined}
                    />
                    {fieldErrors.email ? (
                      <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.email}</p>
                    ) : null}
                  </div>

                  <div>
                    <label htmlFor="registerPassword" className="block text-xs sm:text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      {t.enterprise.form.password}
                    </label>
                    <PasswordInput
                      id="registerPassword"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearFieldError('password');
                      }}
                      placeholder={t.enterprise.form.passwordPlaceholder}
                      show={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                      minLength={6}
                      autoComplete="new-password"
                      hasError={Boolean(fieldErrors.password)}
                    />
                    {fieldErrors.password ? (
                      <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.password}</p>
                    ) : null}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      {t.enterprise.form.confirmPassword}
                    </label>
                    <PasswordInput
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        clearFieldError('confirmPassword');
                      }}
                      placeholder={t.enterprise.form.confirmPasswordPlaceholder}
                      show={showConfirmPassword}
                      onToggle={() => setShowConfirmPassword((v) => !v)}
                      minLength={6}
                      autoComplete="new-password"
                      hasError={Boolean(fieldErrors.confirmPassword)}
                    />
                    {fieldErrors.confirmPassword ? (
                      <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.confirmPassword}</p>
                    ) : null}
                  </div>

                  <TermsAgreementCheckbox
                    checked={acceptedTerms}
                    onChange={(value) => {
                      setAcceptedTerms(value);
                      clearFieldError('terms');
                    }}
                    id="enterprise-terms-agreement"
                  />
                  {fieldErrors.terms ? (
                    <p role="alert" className="text-xs font-medium text-error-600">{fieldErrors.terms}</p>
                  ) : null}

                  {submitError ? (
                    <p role="alert" className="rounded-2xl border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700">
                      {submitError}
                    </p>
                  ) : null}

                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                    disabled={loading}
                    icon={ArrowRight}
                    iconPosition="right"
                    className="w-full py-2.5 sm:py-4 mt-4 sm:mt-6"
                  >
                    {t.enterprise.form.submit}
                  </Button>
                </motion.form>
              )}
            </AnimatePresence>

            <p className="text-center text-sm text-secondary-500 mt-6">
              {mode === 'login' ? t.enterprise.login.noAccount : t.enterprise.form.alreadyHaveAccount}{' '}
              <button
                type="button"
                onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                className="text-orange-600 font-semibold hover:text-orange-700 transition-colors"
              >
                {mode === 'login' ? t.enterprise.form.tab : t.enterprise.login.tab}
              </button>
            </p>

            <p className="text-center text-sm text-secondary-500 mt-3">
              {t.enterprise.nav.userPortalPrompt}{' '}
              <Link to="/login" className="text-orange-600 font-semibold hover:text-orange-700">
                {t.enterprise.nav.userSignIn}
              </Link>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default EnterpriseLandingPage;
